import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import ClientLayout from '../../components/layout/ClientLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { 
  Loader2, CheckCircle2, Clock, Dumbbell, Heart, RotateCcw, Play, 
  Timer, Target, Calendar, Trophy, Video, ChevronRight, ChevronLeft,
  ArrowRight, Flame, Zap, X, AlertTriangle
} from 'lucide-react';
import { RestTimer } from '../../components/ui/rest-timer';
import MesocycleEndScreen from '../../components/MesocycleEndScreen';
import RestMode from '../../components/RestMode';
import MindfulConnection from '../../components/MindfulConnection';
import MindfulCooldownBlock from '../../components/MindfulCooldownBlock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MOODS = [
  { value: 'excellent', emoji: '😀', label: 'Excelente' },
  { value: 'good', emoji: '😊', label: 'Bien' },
  { value: 'neutral', emoji: '😐', label: 'Normal' },
  { value: 'tired', emoji: '😓', label: 'Cansado' },
  { value: 'exhausted', emoji: '😫', label: 'Agotado' },
];

// Calentamiento estándar
const STANDARD_WARMUP = {
  name: "Calentamiento / Activación",
  duration: "7 min",
  description: "Preparar el cuerpo de forma integral antes del trabajo.",
  exercises: [
    { order: 1, name: "Rotación de tobillos", duration: "30s" },
    { order: 2, name: "Rotación de rodillas", duration: "30s" },
    { order: 3, name: "Movilidad de cadera", duration: "30s" },
    { order: 4, name: "Tobillo + rodilla + cadera con brazos", duration: "30s" },
    { order: 5, name: "Rotación de hombro", duration: "30s" },
    { order: 6, name: "Apertura/cierre de brazos", duration: "30s" },
    { order: 7, name: "Cuello controlado", duration: "30s" },
    { order: 8, name: "Balanceo buceo → press francés", duration: "30s" },
    { order: 9, name: "Elevaciones laterales", duration: "30s" },
    { order: 10, name: "Elevaciones frontales + extensión gemelo", duration: "30s" },
    { order: 11, name: "Sentadilla muy controlada", duration: "30s" },
    { order: 12, name: "Peso muerto tipo senior", duration: "30s" },
  ]
};

const ClientCurrentSession = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sessionData, setSessionData] = useState(null);
  const [error, setError] = useState(null);
  const [showMesocycleEnd, setShowMesocycleEnd] = useState(false);
  
  // Training state
  const [phase, setPhase] = useState('summary'); // 'summary', 'warmup', 'training', 'complete'
  const [currentExerciseIndex, setCurrentExerciseIndex] = useState(0);
  const [exerciseLogs, setExerciseLogs] = useState([]);
  const [selectedMood, setSelectedMood] = useState(null);
  const [generalNotes, setGeneralNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showRestTimer, setShowRestTimer] = useState(false);
  const [restSeconds, setRestSeconds] = useState(60);
  
  // Rest Mode states
  const [showRestMode, setShowRestMode] = useState(false);
  const [restModeEnabled, setRestModeEnabled] = useState(false);
  const [restProgram, setRestProgram] = useState('respiracion_4_6');
  const [currentSeriesIndex, setCurrentSeriesIndex] = useState(0);
  const [restLogs, setRestLogs] = useState([]);

  // Mindful Connection (Conexión Cuerpo) - capa entre ejercicios + cooldown final
  const [mindfulSettings, setMindfulSettings] = useState({
    enabled: true,
    show_between_exercises: true,
    show_cooldown_block: true
  });
  const [mindfulItem, setMindfulItem] = useState(null);
  const [showMindfulConnection, setShowMindfulConnection] = useState(false);
  const [usedMindfulIds, setUsedMindfulIds] = useState([]);
  const [cooldownBlock, setCooldownBlock] = useState(null);
  const [showCooldownBlock, setShowCooldownBlock] = useState(false);
  
  // Datos de sesión anterior para comparativa
  const [previousSessionData, setPreviousSessionData] = useState(null);

  // Estado de error del video del ejercicio actual (declarativo, no manipula DOM)
  const [videoLoadError, setVideoLoadError] = useState(false);

  // Reset del error al cambiar de ejercicio
  useEffect(() => {
    setVideoLoadError(false);
  }, [currentExerciseIndex]);

  useEffect(() => {
    fetchCurrentSession();
    checkMesocycleStatus();
    fetchPreviousSessionData();
    checkRestModeStatus();
    restoreSessionFromLocalStorage();
    fetchExerciseVideos();
    fetchMindfulSettings();
  }, []);

  // Cargar mapa de videos de la biblioteca (clave estricta: exercise_id GF-XXX)
  // Arquitectura "1 ID = 1 Video": NO se hace matching por nombre ni fuzzy.
  const [exerciseVideoMap, setExerciseVideoMap] = useState({});

  const fetchExerciseVideos = async () => {
    try {
      const response = await axios.get(`${API}/exercises`);
      const videoMap = {};
      (response.data || []).forEach(ex => {
        // En library_v2: ex.id === ex.exercise_id === "GF-XXX"
        const key = ex.exercise_id || ex.id;
        if (key && ex.video_url) {
          videoMap[key] = ex.video_url;
        }
      });
      setExerciseVideoMap(videoMap);
    } catch (e) {
      console.error('Error loading exercise videos:', e);
    }
  };

  // Helper: obtener video_url para un ejercicio.
  // Arquitectura ESTRICTA: solo por exercise_id (GF-XXX). NO fuzzy matching.
  // Si no hay video, devuelve '' → se muestra el fallback "Estamos trabajando…".
  const getVideoUrl = (exercise) => {
    if (!exercise) return '';
    // 1) video_url directo del plan (si fue propagado)
    if (exercise.video_url) return exercise.video_url;
    // 2) lookup estricto por exercise_id en la librería v2
    if (exercise.exercise_id && exerciseVideoMap[exercise.exercise_id]) {
      return exerciseVideoMap[exercise.exercise_id];
    }
    return '';
  };
  
  // ========== AUTO-GUARDADO EN LOCALSTORAGE ==========
  // Guardar progreso cada vez que cambian los logs de ejercicios
  useEffect(() => {
    if (exerciseLogs.length > 0 && phase === 'training') {
      const saveData = {
        exerciseLogs,
        selectedMood,
        generalNotes,
        currentExerciseIndex,
        currentSetIndex: currentSeriesIndex,
        timestamp: new Date().toISOString(),
        plan_id: sessionData?.plan_id,
        session_number: sessionData?.session_number,
        week_number: sessionData?.week_number
      };
      try {
        localStorage.setItem('gf_session_progress', JSON.stringify(saveData));
      } catch (e) {
        // localStorage full - silently fail
      }
    }
  }, [exerciseLogs, selectedMood, generalNotes, currentExerciseIndex, currentSeriesIndex, phase]);

  // Guardar al cerrar pestaña/app
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (phase === 'training' && exerciseLogs.length > 0) {
        const logData = buildLogData();
        if (logData && logData.exercises.length > 0) {
          const token = localStorage.getItem('token');
          navigator.sendBeacon(
            `${API}/session-logs-beacon?token=${token}`,
            new Blob([JSON.stringify({ ...logData, auto_saved: true })], { type: 'application/json' })
          );
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden' && phase === 'training' && exerciseLogs.length > 0) {
        const logData = buildLogData();
        if (logData && logData.exercises.length > 0) {
          const token = localStorage.getItem('token');
          navigator.sendBeacon(
            `${API}/session-logs-beacon?token=${token}`,
            new Blob([JSON.stringify({ ...logData, auto_saved: true })], { type: 'application/json' })
          );
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [phase, exerciseLogs, sessionData]);

  const restoreSessionFromLocalStorage = () => {
    try {
      const saved = localStorage.getItem('gf_session_progress');
      if (!saved) return;
      const data = JSON.parse(saved);
      // Only restore if less than 4 hours old
      const age = Date.now() - new Date(data.timestamp).getTime();
      if (age > 4 * 60 * 60 * 1000) {
        localStorage.removeItem('gf_session_progress');
        return;
      }
      // Will be applied after session data loads
      window._gf_restored_session = data;
    } catch (e) {
      localStorage.removeItem('gf_session_progress');
    }
  };

  const buildLogData = () => {
    if (!sessionData) return null;
    const strengthExercises = exerciseLogs
      .filter(log => log.type === 'strength')
      .map(log => ({
        exercise_id: log.exercise_id || '',
        exercise_name: log.exercise_name || 'Sin nombre',
        load: log.sets?.filter(s => s.load && s.load !== '').length > 0
          ? log.sets.filter(s => s.load && s.load !== '').reduce((sum, s) => sum + parseFloat(s.load || 0), 0) / log.sets.filter(s => s.load && s.load !== '').length
          : null,
        rpe: log.sets?.filter(s => s.rpe && s.rpe !== '').length > 0
          ? Math.round(log.sets.filter(s => s.rpe && s.rpe !== '').reduce((sum, s) => sum + parseInt(s.rpe || 0), 0) / log.sets.filter(s => s.rpe && s.rpe !== '').length)
          : null,
        notes: log.exercise_notes || '',
        sets: log.sets?.map(s => ({
          load: s.load ? parseFloat(s.load) : 0,
          rpe: s.rpe ? parseInt(s.rpe) : 0
        })) || [],
        target_sets: log.target_sets,
        target_reps: log.target_reps,
        completed: true
      }));
    
    return {
      plan_id: sessionData?.plan_id || '',
      week_number: sessionData?.week_number || 1,
      session_id: sessionData?.session?.id || `session-${sessionData?.session_number || 1}`,
      session_name: sessionData?.session?.name || 'Sesión',
      session_number: sessionData?.session_number || 1,
      exercises: strengthExercises,
      cardio: [],
      circuits: [],
      mood: selectedMood || 'neutral',
      general_notes: generalNotes || ''
    };
  };
  
  // Pre-rellenar cargas cuando llegan los datos de la sesión anterior
  useEffect(() => {
    if (!previousSessionData?.has_previous || !sessionData?.session) return;
    
    setExerciseLogs(prev => {
      if (prev.length === 0) return prev;
      let changed = false;
      const updated = prev.map(log => {
        if (log.type !== 'strength') return log;
        const prevEx = previousSessionData.data?.[log.exercise_id];
        if (!prevEx || !prevEx.loads_per_set?.length) return log;
        
        // Solo pre-rellenar si los sets están vacíos (no sobreescribir datos del usuario)
        const allEmpty = log.sets.every(s => !s.load && s.load !== 0);
        if (!allEmpty) return log;
        
        changed = true;
        const newSets = log.sets.map((s, idx) => {
          const prevLoad = prevEx.loads_per_set[idx];
          return {
            ...s,
            load: prevLoad && prevLoad > 0 ? prevLoad.toString() : s.load
          };
        });
        return { ...log, sets: newSets };
      });
      return changed ? updated : prev;
    });
  }, [previousSessionData, sessionData]);
  
  // Verificar si el cliente tiene Rest Mode habilitado y su programa
  const checkRestModeStatus = async () => {
    try {
      const response = await axios.get(`${API}/me/rest-mode-status`);
      setRestModeEnabled(response.data.rest_mode_enabled);
      
      // También obtener el programa de descanso del usuario
      const restResponse = await axios.get(`${API}/me/rest-onboarding-status`);
      if (restResponse.data.rest_program) {
        setRestProgram(restResponse.data.rest_program);
      }
    } catch (error) {
      console.error('Error checking rest mode status:', error);
    }
  };
  
  // Obtener datos de la sesión anterior (misma sesión, semana anterior)
  const fetchPreviousSessionData = async () => {
    try {
      const response = await axios.get(`${API}/me/previous-session-data`);
      if (response.data.has_previous) {
        setPreviousSessionData(response.data);
      }
    } catch (error) {
      console.error('Error fetching previous session data:', error);
    }
  };

  const checkMesocycleStatus = async () => {
    try {
      const response = await axios.get(`${API}/me/mesocycle-status`);
      // Si completó el mesociclo pero no ha aprobado el test, mostrar pantalla
      if (response.data.mesocycle_completed && !response.data.test_passed) {
        setShowMesocycleEnd(true);
      }
    } catch (error) {
      console.error('Error checking mesocycle status:', error);
    }
  };

  const fetchCurrentSession = async () => {
    try {
      const response = await axios.get(`${API}/me/current-session`);
      setSessionData(response.data);
      
      if (response.data.session) {
        initializeExerciseLogs(response.data.session, response.data);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Error al cargar sesión');
    } finally {
      setLoading(false);
    }
  };

  const initializeExerciseLogs = (session, currentSessionData) => {
    try {
      const logs = [];

      // Builders por tipo de bloque (para respetar el orden del PlanBuilder)
      const pushStrength = () => {
        if (session.exercises && Array.isArray(session.exercises)) {
          session.exercises.forEach(ex => {
            const numSets = parseInt(ex.sets) || 3;
            const sets = Array(numSets).fill(null).map(() => ({ load: '', rpe: '' }));
            logs.push({
              type: 'strength',
              exercise_id: ex.exercise_id || '',
              exercise_name: ex.exercise_name || ex.name || 'Ejercicio sin nombre',
              target_sets: numSets,
              target_reps: ex.reps || ex.target_reps || '',
              target_rest: ex.rest || '60s',
              target_tempo: ex.tempo || '',
              notes: ex.notes || '',
              video_url: ex.video_url || null,
              sets: sets,
              exercise_notes: ''
            });
          });
        }
      };

      const pushCardio = () => {
        if (session.cardio_blocks && Array.isArray(session.cardio_blocks)) {
          session.cardio_blocks.forEach(cardio => {
            logs.push({
              type: 'cardio',
              machine: cardio.machine || 'Cardio',
              target_time: cardio.total_time || cardio.time || '20 min',
              intensity: cardio.intensity || 'Moderada',
              notes: cardio.notes || '',
              completed: false,
              actual_time: '',
              cardio_notes: ''
            });
          });
        }
        // Si hay cardio como objeto directo (no array)
        if (session.cardio && typeof session.cardio === 'object' && !Array.isArray(session.cardio)) {
          logs.push({
            type: 'cardio',
            machine: session.cardio.machine || 'Cardio',
            target_time: session.cardio.total_time || session.cardio.time || '20 min',
            intensity: session.cardio.intensity || 'Moderada',
            notes: session.cardio.notes || '',
            completed: false,
            actual_time: '',
            cardio_notes: ''
          });
        }
      };

      const pushCircuit = () => {
        if (session.circuit_blocks && Array.isArray(session.circuit_blocks)) {
          session.circuit_blocks.forEach(circuit => {
            logs.push({
              type: 'circuit',
              name: circuit.name || 'Circuito',
              rounds: circuit.rounds || 3,
              exercises: circuit.exercises || [],
              exercise_time: circuit.exercise_time || '30s',
              rest_between_exercises: circuit.rest_between_exercises || '15s',
              rest_between_rounds: circuit.rest_between_rounds || '60s',
              completed: false,
              rounds_completed: 0,
              circuit_notes: ''
            });
          });
        }
      };

      // Respetar el orden de bloques configurado en el PlanBuilder
      // (warmup/core/stretching no producen logs paso-a-paso, se muestran en el resumen)
      const blockOrder = session.block_order && session.block_order.length > 0
        ? session.block_order
        : ['strength', 'cardio', 'circuit'];

      const seen = new Set();
      blockOrder.forEach(blockType => {
        if (seen.has(blockType)) return;
        seen.add(blockType);
        if (blockType === 'strength') pushStrength();
        else if (blockType === 'cardio') pushCardio();
        else if (blockType === 'circuit') pushCircuit();
      });

      // Fallback: si block_order no incluye un bloque que sí existe, agrégalo al final
      if (!seen.has('strength')) pushStrength();
      if (!seen.has('cardio')) pushCardio();
      if (!seen.has('circuit')) pushCircuit();
      
      console.log('initializeExerciseLogs - logs creados:', logs.length);
      
      // Restaurar progreso guardado si existe
      try {
        const restored = window._gf_restored_session;
        if (restored && currentSessionData &&
            restored.plan_id === currentSessionData.plan_id && 
            restored.session_number === currentSessionData.session_number &&
            restored.exerciseLogs?.length > 0) {
          const restoredMap = {};
          restored.exerciseLogs.forEach(rl => {
            if (rl.exercise_id) restoredMap[rl.exercise_id] = rl;
          });
          
          logs.forEach(log => {
            const saved = restoredMap[log.exercise_id];
            if (saved?.sets) {
              log.sets = log.sets.map((s, idx) => ({
                ...s,
                load: saved.sets[idx]?.load || s.load,
                rpe: saved.sets[idx]?.rpe || s.rpe
              }));
            }
          });
          
          if (restored.selectedMood) setSelectedMood(restored.selectedMood);
          if (restored.generalNotes) setGeneralNotes(restored.generalNotes);
          toast.info('Progreso anterior restaurado');
        }
        delete window._gf_restored_session;
        localStorage.removeItem('gf_session_progress');
      } catch (restoreErr) {
        console.error('Error restaurando sesión:', restoreErr);
        localStorage.removeItem('gf_session_progress');
      }
      
      // Si no hay ejercicios, crear un log vacío de fallback
      if (logs.length === 0) {
        console.warn('No se encontraron ejercicios en la sesión');
        logs.push({
          type: 'strength',
          exercise_id: 'fallback',
          exercise_name: 'Sesión sin ejercicios definidos',
          target_sets: 0,
          target_reps: '-',
          target_rest: '-',
          notes: 'Esta sesión no tiene ejercicios configurados. Contacta con tu entrenador.',
          sets: [],
          exercise_notes: ''
        });
      }
      
      setExerciseLogs(logs);
    } catch (error) {
      console.error('Error initializing exercise logs:', error);
      // Crear un log de fallback en caso de error
      setExerciseLogs([{
        type: 'strength',
        exercise_id: 'error',
        exercise_name: 'Error al cargar ejercicios',
        target_sets: 0,
        target_reps: '-',
        notes: 'Hubo un error al cargar los ejercicios. Por favor recarga la página.',
        sets: [],
        exercise_notes: ''
      }]);
    }
  };

  const updateSetLog = (exerciseIndex, setIndex, field, value) => {
    setExerciseLogs(prev => {
      const updated = [...prev];
      if (updated[exerciseIndex].sets) {
        updated[exerciseIndex].sets[setIndex][field] = value;
      }
      return updated;
    });
  };

  const updateLog = (exerciseIndex, field, value) => {
    setExerciseLogs(prev => {
      const updated = [...prev];
      updated[exerciseIndex][field] = value;
      return updated;
    });
  };

  // Completar una serie y activar Rest Mode
  const completeSet = (setIndex) => {
    const currentLog = exerciseLogs[currentExerciseIndex];

    // Marcar la serie como completada (siempre — independiente del descanso consciente)
    setExerciseLogs(prev => {
      const updated = [...prev];
      if (updated[currentExerciseIndex].sets) {
        updated[currentExerciseIndex].sets[setIndex].completed = true;
      }
      return updated;
    });

    // Guardar índice de serie actual
    setCurrentSeriesIndex(setIndex);

    // Solo si el descanso consciente está habilitado para este cliente,
    // activar el modal de RestMode entre series. Si no, el cliente continúa libre.
    if (restModeEnabled && setIndex < currentLog.sets.length - 1) {
      const restTime = parseRestToSeconds(currentLog.target_rest);
      setRestSeconds(restTime);
      setShowRestMode(true);
    }
  };

  // Cuando Rest Mode termina
  const handleRestModeComplete = () => {
    setShowRestMode(false);
    // El usuario continúa con la siguiente serie
  };

  // Cuando el usuario salta el Rest Mode
  const handleRestModeSkip = () => {
    setShowRestMode(false);
  };

  const parseRestToSeconds = (rest) => {
    if (!rest) return 60;
    const match = rest.match(/(\d+)/);
    if (match) {
      const num = parseInt(match[1]);
      if (rest.toLowerCase().includes('min')) return num * 60;
      return num;
    }
    return 60;
  };

  const startWarmup = () => {
    setPhase('warmup');
  };

  const startTraining = () => {
    setPhase('training');
    setCurrentExerciseIndex(0);
  };

  // ===== Conexión Cuerpo (Mindful Rest) =====
  const fetchMindfulSettings = async () => {
    try {
      const res = await axios.get(`${API}/mindful-rest/settings`);
      if (res.data) setMindfulSettings(res.data);
    } catch (e) {
      console.warn('No se pudo cargar mindful settings, usando defaults', e);
    }
  };

  const fetchNextMindfulItem = async () => {
    try {
      const exclude = usedMindfulIds.join(',');
      const res = await axios.get(`${API}/mindful-rest/next?exclude=${encodeURIComponent(exclude)}`);
      const item = res.data?.item;
      if (res.data?.cycle_reset) {
        setUsedMindfulIds([]);
      }
      if (item) {
        setUsedMindfulIds(prev => [...prev, item.id]);
      }
      return item || null;
    } catch (e) {
      console.warn('No se pudo cargar mindful item', e);
      return null;
    }
  };

  const fetchCooldownBlock = async () => {
    try {
      const res = await axios.get(`${API}/mindful-rest/cooldown`);
      return res.data || null;
    } catch (e) {
      console.warn('No se pudo cargar cooldown block', e);
      return null;
    }
  };

  const handleMindfulComplete = () => {
    setShowMindfulConnection(false);
    setMindfulItem(null);
    goToNextExercise();
  };

  const handleMindfulSkip = () => {
    setShowMindfulConnection(false);
    setMindfulItem(null);
    goToNextExercise();
  };

  const handleCooldownFinish = () => {
    setShowCooldownBlock(false);
    setCooldownBlock(null);
    setShowCompleteDialog(true);
    setPhase('complete');
  };

  const handleCooldownSkipAll = () => {
    setShowCooldownBlock(false);
    setCooldownBlock(null);
    setShowCompleteDialog(true);
    setPhase('complete');
  };

  const nextExercise = async () => {
    // Si Conexión Cuerpo está habilitada y hay siguiente ejercicio, mostramos el modal
    const isLastExercise = currentExerciseIndex >= exerciseLogs.length - 1;
    const useMindful = mindfulSettings.enabled && mindfulSettings.show_between_exercises && !isLastExercise;

    if (useMindful) {
      const item = await fetchNextMindfulItem();
      if (item) {
        setMindfulItem(item);
        setShowMindfulConnection(true);
        return;
      }
      // Si falla la carga, fallback al flujo clásico
    }
    goToNextExercise();
  };

  const goToNextExercise = async () => {
    if (currentExerciseIndex < exerciseLogs.length - 1) {
      setCurrentExerciseIndex(currentExerciseIndex + 1);
      setShowRestTimer(false);
    } else {
      // Última: si Cooldown habilitado, mostramos bloque de cierre antes del diálogo
      if (mindfulSettings.enabled && mindfulSettings.show_cooldown_block) {
        const block = await fetchCooldownBlock();
        if (block && block.exercises?.length > 0) {
          setCooldownBlock(block);
          setShowCooldownBlock(true);
          return;
        }
      }
      setShowCompleteDialog(true);
      setPhase('complete');
    }
  };

  const previousExercise = () => {
    if (currentExerciseIndex > 0) {
      setCurrentExerciseIndex(currentExerciseIndex - 1);
    }
  };

  const submitSession = async () => {
    if (!selectedMood) {
      toast.error('Selecciona cómo te sientes');
      return;
    }
    
    setSubmitting(true);
    try {
      // Ejercicios de fuerza
      const strengthExercises = exerciseLogs
        .filter(log => log.type === 'strength')
        .map(log => ({
          exercise_id: log.exercise_id || '',
          exercise_name: log.exercise_name || 'Sin nombre',
          // Guardar carga promedio para compatibilidad
          load: log.sets?.filter(s => s.load && s.load !== '').length > 0 
            ? log.sets.filter(s => s.load && s.load !== '').reduce((sum, s) => sum + parseFloat(s.load || 0), 0) / log.sets.filter(s => s.load && s.load !== '').length 
            : null,
          rpe: log.sets?.filter(s => s.rpe && s.rpe !== '').length > 0
            ? Math.round(log.sets.filter(s => s.rpe && s.rpe !== '').reduce((sum, s) => sum + parseInt(s.rpe || 0), 0) / log.sets.filter(s => s.rpe && s.rpe !== '').length)
            : null,
          notes: log.exercise_notes || '',
          // Guardar datos de cada serie individualmente
          sets: log.sets?.map(s => ({
            load: s.load ? parseFloat(s.load) : 0,
            rpe: s.rpe ? parseInt(s.rpe) : 0
          })) || [],
          // Datos adicionales para historial
          target_sets: log.target_sets,
          target_reps: log.target_reps,
          completed: true
        }));

      // Ejercicios de cardio
      const cardioExercises = exerciseLogs
        .filter(log => log.type === 'cardio')
        .map(log => ({
          type: 'cardio',
          machine: log.machine,
          target_time: log.target_time,
          actual_time: log.actual_time || log.target_time,
          intensity: log.intensity,
          notes: log.cardio_notes || '',
          completed: log.completed
        }));

      // Circuitos
      const circuitExercises = exerciseLogs
        .filter(log => log.type === 'circuit')
        .map(log => ({
          type: 'circuit',
          name: log.name,
          rounds: log.rounds,
          rounds_completed: log.rounds_completed || log.rounds,
          exercises: log.exercises,
          notes: log.circuit_notes || '',
          completed: log.completed
        }));

      const logData = {
        plan_id: sessionData?.plan_id || '',
        week_number: sessionData?.week_number || 1,
        session_id: sessionData?.session?.id || `session-${sessionData?.session_number || 1}`,
        session_name: sessionData?.session?.name || 'Sesión',
        session_number: sessionData?.session_number || 1,
        exercises: strengthExercises,
        cardio: cardioExercises,
        circuits: circuitExercises,
        mood: selectedMood,
        general_notes: generalNotes,
        // Metadata para debugging
        total_exercises_logged: exerciseLogs.length,
        strength_count: strengthExercises.length,
        cardio_count: cardioExercises.length,
        circuit_count: circuitExercises.length
      };
      
      console.log('Guardando sesión:', logData); // Debug
      
      // Guardar session log primero - CRÍTICO para tracking de cargas
      try {
        const logResponse = await axios.post(`${API}/session-logs`, logData);
        console.log('Session log guardado:', logResponse.data?.id);
      } catch (logError) {
        console.error('Error guardando session log:', logError?.response?.data || logError.message);
        toast.error('Error guardando las cargas. Se reintentará al completar.');
        // Reintentar una vez
        try {
          await axios.post(`${API}/session-logs`, logData);
          console.log('Session log guardado en reintento');
        } catch (retryError) {
          console.error('Error en reintento de session log:', retryError?.response?.data || retryError.message);
          toast.error('No se pudieron guardar las cargas. Contacta a tu entrenador.');
        }
      }
      
      // Completar sesión
      try {
        const completeResponse = await axios.post(`${API}/me/complete-session`);
        console.log('Sesión completada:', completeResponse.data);
        
        // Verificar si se completó un mesociclo
        if (completeResponse.data?.mesociclo_completado) {
          toast.success(`🏆 ¡Mesociclo ${completeResponse.data.mesociclo_completado.numero} completado!`);
        } else {
          toast.success('¡Sesión completada! 💪');
        }
        // Limpiar auto-guardado
        localStorage.removeItem('gf_session_progress');
      } catch (completeError) {
        console.error('Error en complete-session:', completeError);
        // Mostrar mensaje pero no bloquear
        toast.warning('Sesión guardada, pero hubo un problema actualizando el progreso.');
      }
      
      // Resetear estado completamente antes de volver al summary
      setShowCompleteDialog(false);
      setSelectedMood(null);
      setGeneralNotes('');
      setCurrentExerciseIndex(0);
      setPhase('summary');
      setLoading(true); // Mostrar loading mientras recarga
      
      // Recargar datos de la sesión
      try {
        await fetchCurrentSession();
      } catch (fetchError) {
        console.error('Error recargando sesión:', fetchError);
        // Si falla la recarga, al menos tenemos el estado limpio
        setLoading(false);
        setSessionData(null);
      }
    } catch (error) {
      console.error('Error general al guardar sesión:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar sesión. Intenta de nuevo.');
      // En caso de error, cerrar dialog y volver a summary para que puedan reintentar
      setShowCompleteDialog(false);
      setPhase('summary');
    } finally {
      setSubmitting(false);
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <ClientLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </ClientLayout>
    );
  }

  // Show Mesocycle End Screen
  if (showMesocycleEnd) {
    return (
      <ClientLayout>
        <MesocycleEndScreen 
          onClose={() => setShowMesocycleEnd(false)}
          onAdvance={() => {
            setShowMesocycleEnd(false);
            fetchCurrentSession();
          }}
        />
      </ClientLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <p className="text-muted-foreground">{error}</p>
        </div>
      </ClientLayout>
    );
  }

  // No session available
  if (!sessionData?.session) {
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <Trophy className="h-16 w-16 text-primary mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {sessionData?.plan_completed ? '¡Plan Completado!' : sessionData?.message || 'No hay sesión disponible'}
          </h2>
          <p className="text-muted-foreground">
            {sessionData?.plan_completed 
              ? 'Has completado todas las sesiones de tu plan. ¡Felicidades!'
              : 'Contacta con tu entrenador para que te asigne un plan.'}
          </p>
        </div>
      </ClientLayout>
    );
  }

  const session = sessionData.session;
  const currentLog = exerciseLogs[currentExerciseIndex];
  const progress = exerciseLogs.length > 0 ? ((currentExerciseIndex + 1) / exerciseLogs.length) * 100 : 0;

  // ============== COMPLETE DIALOG (JSX inline - NO redefinir en cada render) ==============
  const completeDialogJsx = (
    <Dialog open={showCompleteDialog} onOpenChange={(open) => {
      if (!open) {
        setShowCompleteDialog(false);
      }
    }}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center text-xl">
            <Trophy className="h-12 w-12 text-primary mx-auto mb-2" />
            ¡Sesión Completada!
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium">¿Cómo te sientes?</Label>
            <div className="flex justify-center gap-2 mt-2">
              {MOODS.map((mood) => (
                <button
                  key={mood.value}
                  onClick={() => setSelectedMood(mood.value)}
                  className={`p-3 rounded-lg transition-all ${
                    selectedMood === mood.value
                      ? 'bg-primary/20 ring-2 ring-primary scale-110'
                      : 'bg-zinc-800/50 hover:bg-zinc-800'
                  }`}
                >
                  <span className="text-2xl">{mood.emoji}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label>Notas generales (opcional)</Label>
            <Textarea
              placeholder="¿Cómo fue tu entrenamiento hoy?"
              value={generalNotes}
              onChange={(e) => setGeneralNotes(e.target.value)}
              rows={3}
              className="mt-1"
            />
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button 
            onClick={submitSession}
            disabled={submitting || !selectedMood}
            className="w-full h-12 bg-primary"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Guardar y Completar
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
  // eslint-disable-next-line no-unused-vars
  const CompleteDialog = () => completeDialogJsx;

  // ============== PHASE: SUMMARY ==============
  if (phase === 'summary') {
    return (
      <ClientLayout>
        {completeDialogJsx}
        <div className="max-w-2xl mx-auto space-y-6 p-4">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-primary/20 rounded-full">
              <Calendar className="h-4 w-4 text-primary" />
              <span className="text-sm text-primary font-medium">
                Semana {sessionData.week_number} • Sesión {sessionData.session_number}
              </span>
            </div>
            <h1 className="text-3xl font-bold text-foreground">
              {session.name || `Sesión ${sessionData.session_number}`}
            </h1>
            <p className="text-muted-foreground">
              {sessionData.plan_name}
            </p>
          </div>

          {/* Contador del Mesociclo */}
          <Card className="bg-gradient-to-r from-primary/10 to-amber-500/10 border-primary/30" data-testid="mesocycle-counter">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Target className="h-5 w-5 text-primary" />
                  <span className="font-semibold text-foreground">Mesociclo {sessionData.mesocycle}</span>
                </div>
                <div className="text-right">
                  <span className="text-2xl font-bold text-primary">{sessionData.sessions_completed || 0}</span>
                  <span className="text-muted-foreground">/{sessionData.total_sessions_mesocycle || 25}</span>
                </div>
              </div>
              <Progress value={sessionData.mesocycle_progress_percent || 0} className="h-2 mb-2" />
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  {sessionData.sessions_per_week || 5} sesiones/semana × 5 semanas
                </span>
                <span className="text-amber-400 font-medium">
                  {sessionData.sessions_remaining || 0} restantes
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Session Summary Card */}
          <Card className="bg-card border-border overflow-hidden">
            <CardContent className="p-0">
              {(() => {
                // Respetar el orden de bloques configurado en el PlanBuilder
                const blockOrder = session.block_order && session.block_order.length > 0
                  ? session.block_order
                  : ['warmup', 'strength', 'cardio', 'circuit', 'core', 'stretching'];

                const renderBlock = (blockType) => {
                  switch (blockType) {
                    case 'warmup':
                      if (!session.warmup_exercises?.length) return null;
                      return (
                        <div key="warmup" className="p-4 border-b border-border">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-amber-500/20 rounded-lg">
                              <Flame className="h-5 w-5 text-amber-400" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">Calentamiento</h3>
                              <p className="text-sm text-muted-foreground">{session.warmup_exercises.length} ejercicios</p>
                            </div>
                          </div>
                          <div className="space-y-1 pl-12">
                            {session.warmup_exercises.map((ex, idx) => (
                              <div key={idx} className="text-sm text-muted-foreground">
                                {idx + 1}. {ex.name || ex.exercise_name || '—'} {ex.duration ? `• ${ex.duration}` : ''} {ex.reps ? `• ${ex.reps} reps` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    case 'strength':
                      if (!session.exercises?.length) return null;
                      return (
                        <div key="strength" className="p-4 border-b border-border">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-orange-500/20 rounded-lg">
                              <Dumbbell className="h-5 w-5 text-orange-400" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">Bloque de Fuerza</h3>
                              <p className="text-sm text-muted-foreground">{session.exercises.length} ejercicios</p>
                            </div>
                          </div>
                          <div className="space-y-2 pl-12">
                            {session.exercises.map((ex, idx) => {
                              const prevEx = previousSessionData?.data?.[ex.exercise_id];
                              return (
                                <div key={idx} className={`flex items-center gap-2 text-sm ${
                                  ex.is_triseries
                                    ? 'bg-amber-500/10 p-2 rounded-sm border-l-2 border-amber-500'
                                    : ex.is_biseries
                                      ? 'bg-purple-500/10 p-2 rounded-sm border-l-2 border-purple-500'
                                      : ''
                                }`}>
                                  <span className={`w-5 h-5 flex items-center justify-center rounded text-xs font-bold ${
                                    ex.is_triseries
                                      ? 'bg-amber-500/20 text-amber-400'
                                      : ex.is_biseries
                                        ? 'bg-purple-500/20 text-purple-400'
                                        : 'bg-orange-500/10 text-orange-400'
                                  }`}>
                                    {idx + 1}
                                  </span>
                                  <div className="flex-1">
                                    <span className="text-foreground">
                                      {ex.is_triseries && <span className="text-amber-400 mr-1">⚡⚡</span>}
                                      {!ex.is_triseries && ex.is_biseries && <span className="text-purple-400 mr-1">⚡</span>}
                                      {ex.exercise_name}
                                    </span>
                                    {(ex.is_biseries || ex.is_triseries) && ex.biseries_exercise_name && (
                                      <span className="text-purple-300 ml-1">+ {ex.biseries_exercise_name}</span>
                                    )}
                                    {ex.is_triseries && ex.triseries_exercise_name && (
                                      <span className="text-amber-300 ml-1">+ {ex.triseries_exercise_name}</span>
                                    )}
                                  </div>
                                  <span className="text-muted-foreground">{ex.sets}x{ex.reps}</span>
                                  {prevEx && prevEx.max_load > 0 && (
                                    <span className="text-blue-400 font-semibold text-xs bg-blue-500/15 px-2 py-0.5 rounded" data-testid={`summary-prev-load-${idx}`}>
                                      {prevEx.max_load}kg
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    case 'cardio':
                      if (!session.cardio_blocks?.length) return null;
                      return (
                        <div key="cardio" className="p-4 border-b border-border">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-red-500/20 rounded-lg">
                              <Heart className="h-5 w-5 text-red-400" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">Cardio</h3>
                              <p className="text-sm text-muted-foreground">{session.cardio_blocks.length} bloque(s)</p>
                            </div>
                          </div>
                          <div className="space-y-2 pl-12">
                            {session.cardio_blocks.map((cardio, idx) => (
                              <div key={idx} className="text-sm text-muted-foreground">
                                {cardio.machine} • {cardio.total_time} • {cardio.intensity}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    case 'circuit':
                      if (!session.circuit_blocks?.length) return null;
                      return (
                        <div key="circuit" className="p-4 border-b border-border">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-purple-500/20 rounded-lg">
                              <Zap className="h-5 w-5 text-purple-400" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">Circuito</h3>
                              <p className="text-sm text-muted-foreground">{session.circuit_blocks.length} circuito(s)</p>
                            </div>
                          </div>
                          <div className="space-y-2 pl-12">
                            {session.circuit_blocks.map((circuit, idx) => (
                              <div key={idx} className="text-sm text-muted-foreground">
                                <div className="font-medium text-foreground">{circuit.name} • {circuit.rounds} rondas</div>
                                {(circuit.exercises || []).length > 0 && (
                                  <ul className="ml-3 mt-1 space-y-0.5">
                                    {(circuit.exercises || []).map((cex, i) => (
                                      <li key={i}>• {cex.name || cex.exercise_name || '—'} {cex.duration ? `(${cex.duration})` : ''} {cex.reps ? `(${cex.reps} reps)` : ''}</li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    case 'core':
                      if (!session.abdominal_exercises?.length) return null;
                      return (
                        <div key="core" className="p-4 border-b border-border">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-blue-500/20 rounded-lg">
                              <Target className="h-5 w-5 text-blue-400" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">Core / Abdominales</h3>
                              <p className="text-sm text-muted-foreground">{session.abdominal_exercises.length} ejercicios</p>
                            </div>
                          </div>
                          <div className="space-y-1 pl-12">
                            {session.abdominal_exercises.map((ex, idx) => (
                              <div key={idx} className="text-sm text-muted-foreground">
                                {idx + 1}. {ex.name || ex.exercise_name || '—'} {ex.sets ? `• ${ex.sets}x${ex.reps || ''}` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    case 'stretching':
                      if (!session.stretching_exercises?.length) return null;
                      return (
                        <div key="stretching" className="p-4 border-b border-border">
                          <div className="flex items-center gap-3 mb-3">
                            <div className="p-2 bg-green-500/20 rounded-lg">
                              <Heart className="h-5 w-5 text-green-400" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">Estiramientos</h3>
                              <p className="text-sm text-muted-foreground">{session.stretching_exercises.length} ejercicios</p>
                            </div>
                          </div>
                          <div className="space-y-1 pl-12">
                            {session.stretching_exercises.map((ex, idx) => (
                              <div key={idx} className="text-sm text-muted-foreground">
                                {idx + 1}. {ex.name || ex.exercise_name || '—'} {ex.duration ? `• ${ex.duration}` : ''}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    default:
                      return null;
                  }
                };

                return blockOrder.map(renderBlock).filter(Boolean);
              })()}
            </CardContent>
          </Card>

          {/* Start Button */}
          <Button 
            onClick={startWarmup}
            className="w-full h-16 text-lg font-bold bg-primary hover:bg-primary/90"
            data-testid="start-session-button"
          >
            <Play className="h-6 w-6 mr-3" />
            INICIAR SESIÓN
          </Button>
        </div>
      </ClientLayout>
    );
  }

  // ============== PHASE: WARMUP ==============
  if (phase === 'warmup') {
    return (
      <ClientLayout>
        <div className="max-w-2xl mx-auto space-y-6 p-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => setPhase('summary')}>
              <ChevronLeft className="h-5 w-5 mr-1" />
              Volver
            </Button>
            <span className="text-sm text-muted-foreground">Calentamiento</span>
          </div>

          {/* Warmup Card */}
          <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/30">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-500/20 rounded-full mb-4">
                  <Flame className="h-8 w-8 text-amber-400" />
                </div>
                <h2 className="text-2xl font-bold text-foreground">{STANDARD_WARMUP.name}</h2>
                <p className="text-muted-foreground mt-1">{STANDARD_WARMUP.duration}</p>
                <p className="text-sm text-muted-foreground mt-2">{STANDARD_WARMUP.description}</p>
              </div>

              <div className="space-y-2">
                {STANDARD_WARMUP.exercises.map((ex, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-black/20 rounded-lg">
                    <span className="w-8 h-8 flex items-center justify-center bg-amber-500/20 text-amber-400 rounded-full text-sm font-bold">
                      {ex.order}
                    </span>
                    <div className="flex-1">
                      <p className="text-foreground font-medium">{ex.name}</p>
                      {ex.notes && <p className="text-xs text-muted-foreground">{ex.notes}</p>}
                    </div>
                    <span className="text-amber-400 font-mono text-sm">{ex.duration}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Continue Button */}
          <Button 
            onClick={startTraining}
            className="w-full h-14 text-lg font-bold bg-primary hover:bg-primary/90"
          >
            CALENTAMIENTO COMPLETADO
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </ClientLayout>
    );
  }

  // ============== PHASE: TRAINING (Full Screen Exercise View) ==============
  if (phase === 'training') {
    // Verificar que tenemos datos válidos
    if (!currentLog || exerciseLogs.length === 0) {
      return (
        <ClientLayout>
          <div className="flex flex-col items-center justify-center h-64 text-center p-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground mb-4">Cargando ejercicio...</p>
            <Button 
              variant="outline" 
              onClick={() => {
                setPhase('summary');
                fetchCurrentSession();
              }}
            >
              Volver al resumen
            </Button>
          </div>
        </ClientLayout>
      );
    }
    
    // Strength Exercise View - 3 BLOQUES
    if (currentLog.type === 'strength') {
      return (
        <ClientLayout>
          <div className="min-h-screen flex flex-col bg-background">
            {/* Top Progress Bar */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border p-3">
              <div className="flex items-center justify-between mb-2">
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => currentExerciseIndex === 0 ? setPhase('warmup') : previousExercise()}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  {currentExerciseIndex === 0 ? 'Calentamiento' : 'Anterior'}
                </Button>
                <span className="text-sm font-medium text-muted-foreground">
                  {currentExerciseIndex + 1} / {exerciseLogs.length}
                </span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setPhase('complete')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            {/* Exercise Content - 3 BLOQUES */}
            <div className="flex-1 p-4 space-y-4 overflow-y-auto">
              
              {/* ========== BLOQUE 1: NOMBRE + NOTA TÉCNICA ========== */}
              <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 border-amber-500/30">
                <CardContent className="p-4 space-y-3">
                  {/* Exercise Header */}
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-orange-500/20 rounded-lg">
                      <Dumbbell className="h-6 w-6 text-orange-400" />
                    </div>
                    <div className="flex-1">
                      <h1 className="text-xl font-bold text-foreground leading-tight">
                        {currentLog.exercise_name}
                      </h1>
                      <div className="flex flex-wrap items-center gap-2 mt-1 text-sm text-muted-foreground">
                        <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full text-xs font-medium">
                          {currentLog.target_sets} series
                        </span>
                        <span className="px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full text-xs font-medium">
                          {currentLog.target_reps} reps
                        </span>
                        {currentLog.target_rest && (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded-full text-xs font-medium">
                            <Clock className="h-3 w-3 inline mr-1" />
                            {currentLog.target_rest}
                          </span>
                        )}
                        {currentLog.target_tempo && (
                          <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded-full text-xs font-medium">
                            Tempo: {currentLog.target_tempo}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Nota Técnica del Ejercicio */}
                  <div className="border-t border-amber-500/20 pt-3">
                    <p className="text-xs font-semibold text-amber-400 uppercase tracking-wide mb-1">
                      📋 Nota Técnica
                    </p>
                    {currentLog.notes ? (
                      <p className="text-sm text-foreground leading-relaxed">
                        {currentLog.notes}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">
                        Este ejercicio no tiene nota técnica asignada en la librería.
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* ========== BLOQUE 2: VIDEO / MENSAJE GUARDIANSFIT ========== */}
              <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
                <CardContent className="p-0">
                  {(() => {
                    const videoSrc = getVideoUrl(currentLog);
                    const hasVideo = !!videoSrc && !videoLoadError;
                    return hasVideo ? (
                    <div className="relative aspect-video bg-black" key={`video-wrapper-${currentExerciseIndex}`}>
                      <video
                        key={`video-${currentExerciseIndex}-${videoSrc}` /* CRITICAL: fuerza re-mount cuando cambia el ejercicio */}
                        controls
                        playsInline
                        loop
                        muted
                        autoPlay
                        preload="auto"
                        className="w-full h-full object-contain"
                        onError={() => {
                          // Declarativo: marca error, React mostrará el fallback en el próximo render
                          setVideoLoadError(true);
                        }}
                      >
                        {/* IMPORTANTE: el más universal primero (Android/Chrome) y los específicos después (Safari iOS).
                            El orden anterior tenía codecs=hvc1 primero, lo que rompía la reproducción en Xiaomi/MIUI. */}
                        <source src={`${process.env.REACT_APP_BACKEND_URL}${videoSrc}`} type="video/mp4" />
                        <source src={`${process.env.REACT_APP_BACKEND_URL}${videoSrc}`} type="video/mp4; codecs=hvc1" />
                        <source src={`${process.env.REACT_APP_BACKEND_URL}${videoSrc}`} type="video/quicktime" />
                        <source src={`${process.env.REACT_APP_BACKEND_URL}${videoSrc}`} />
                      </video>
                    </div>
                  ) : (
                    <div className="relative aspect-video bg-[#1a1a1a] flex flex-col items-center justify-center" key={`fallback-${currentExerciseIndex}`}>
                      <div className="relative z-10 flex flex-col items-center justify-center p-6">
                        <img 
                          src="https://customer-assets.emergentagent.com/job_fit-guardian/artifacts/dxhfervd_Sin%20t%C3%ADtulo.jpeg"
                          alt="GuardiansFit"
                          className="w-28 h-28 object-contain mb-3 opacity-90"
                        />
                        <div className="text-center space-y-2">
                          <p className="text-primary text-xs font-bold uppercase tracking-[0.15em]">
                            GuardiansFit
                          </p>
                          <p className="text-zinc-300 text-sm font-medium leading-relaxed max-w-[260px]">
                            {videoLoadError
                              ? 'Este video no se puede reproducir en tu dispositivo'
                              : 'Estamos trabajando en la producci\u00f3n de videos para ti'}
                          </p>
                          {videoLoadError && videoSrc && (
                            <a
                              href={`${process.env.REACT_APP_BACKEND_URL}${videoSrc}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-block text-primary text-xs mt-2 underline"
                              data-testid="video-open-direct"
                            >
                              Abrir video directamente
                            </a>
                          )}
                          {!videoLoadError && (
                            <div className="bg-primary/10 border border-primary/20 rounded-lg p-3 mt-2">
                              <p className="text-primary/90 text-xs leading-relaxed">
                                Mientras tanto, usa el plan <span className="font-bold">Descanso Consciente</span> y respira
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                  })()}
                </CardContent>
              </Card>

              {/* ========== BLOQUE 3: COMPARATIVA SESIÓN ANTERIOR ========== */}
              {previousSessionData?.has_previous && previousSessionData.data[currentLog.exercise_id] && (
                <Card className="bg-gradient-to-r from-blue-500/10 to-cyan-500/5 border-blue-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <div className="p-1.5 bg-blue-500/20 rounded-lg">
                        <RotateCcw className="h-4 w-4 text-blue-400" />
                      </div>
                      <h3 className="font-bold text-blue-300 text-sm" data-testid="previous-loads-title">
                        {previousSessionData.source === 'last_session_any' 
                          ? 'TU ÚLTIMA SESIÓN' 
                          : `SEMANA ANTERIOR (S${previousSessionData.previous_week})`
                        }
                      </h3>
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      {previousSessionData.data[currentLog.exercise_id].loads_per_set.map((load, idx) => (
                        <div key={idx} className="bg-blue-500/10 rounded-lg p-2 text-center">
                          <p className="text-xs text-blue-300/70">Serie {idx + 1}</p>
                          <p className="text-lg font-bold text-blue-300">
                            {load > 0 ? `${load} kg` : '-'}
                          </p>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t border-blue-500/20 flex items-center justify-between">
                      <p className="text-xs text-blue-300/70">Peso máximo anterior:</p>
                      <p className="text-lg font-bold text-blue-300">
                        {previousSessionData.data[currentLog.exercise_id].max_load} kg
                      </p>
                    </div>
                    <p className="text-xs text-blue-400 mt-2 text-center">
                      💪 ¡Intenta superar o mantener estas cargas!
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* ========== BLOQUE 4: REGISTRO DE CARGAS ========== */}
              <Card className="bg-card border-primary/30">
                <CardContent className="p-4 space-y-4">
                  {/* Header */}
                  <div className="flex items-center gap-2 pb-2 border-b border-border">
                    <Target className="h-5 w-5 text-primary" />
                    <h3 className="font-bold text-foreground">REGISTRO DE CARGAS</h3>
                    {previousSessionData?.has_previous && previousSessionData.data[currentLog.exercise_id] && (
                      <span className="ml-auto text-xs bg-blue-500/20 text-blue-300 px-2 py-1 rounded">
                        Anterior: {previousSessionData.data[currentLog.exercise_id].max_load} kg
                      </span>
                    )}
                  </div>
                  
                  {/* Series Grid */}
                  <div className="space-y-3">
                    {currentLog.sets.map((set, setIndex) => {
                      // Obtener peso de la semana anterior para esta serie
                      const prevLoads = previousSessionData?.data?.[currentLog.exercise_id]?.loads_per_set || [];
                      const prevLoad = prevLoads[setIndex];
                      
                      return (
                        <div key={setIndex} className="flex items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                          <div className={`w-14 h-14 flex items-center justify-center rounded-lg font-bold text-xl border ${
                            set.completed 
                              ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                              : 'bg-primary/20 text-primary border-primary/30'
                          }`}>
                            {set.completed ? <CheckCircle2 className="h-6 w-6" /> : `S${setIndex + 1}`}
                          </div>
                          <div className="flex-1 grid grid-cols-2 gap-2">
                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <Label className="text-xs text-muted-foreground">CARGA (kg)</Label>
                                {prevLoad > 0 && (
                                  <span className="text-xs text-blue-400 font-medium">
                                    Ant: {prevLoad}kg
                                  </span>
                                )}
                              </div>
                              <Input
                                type="text"
                                inputMode="decimal"
                                pattern="[0-9]*[.,]?[0-9]*"
                                placeholder={prevLoad > 0 ? prevLoad.toString() : "0"}
                                value={set.load}
                                onChange={(e) => {
                                  const v = e.target.value.replace(',', '.').replace(/[^0-9.]/g, '');
                                  updateSetLog(currentExerciseIndex, setIndex, 'load', v);
                                }}
                                className={`h-14 text-xl font-bold text-center bg-zinc-900 border-zinc-700 focus:border-primary ${
                                  prevLoad > 0 && parseFloat(set.load) >= prevLoad ? 'border-green-500/50' : ''
                                }`}
                                disabled={set.completed}
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-muted-foreground mb-1 block">RPE (1-10)</Label>
                              <Input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                maxLength={2}
                                placeholder="7"
                                value={set.rpe}
                                onChange={(e) => {
                                  let v = e.target.value.replace(/[^0-9]/g, '');
                                  if (v.length > 2) v = v.slice(0, 2);
                                  // Clamp 1-10 (permite borrar a vacio)
                                  if (v !== '') {
                                    const n = parseInt(v, 10);
                                    if (!isNaN(n)) {
                                      if (n > 10) v = '10';
                                      else if (n < 1 && v.length === 1) v = v; // permite escribir "1" para luego "10"
                                    }
                                  }
                                  updateSetLog(currentExerciseIndex, setIndex, 'rpe', v);
                                }}
                                className="h-14 text-xl font-bold text-center bg-zinc-900 border-zinc-700 focus:border-primary"
                                disabled={set.completed}
                              />
                            </div>
                          </div>
                          {/* Botón completar serie - SIEMPRE disponible para registrar la carga.
                              El descanso consciente se activa SOLO si restModeEnabled=true. */}
                          {!set.completed && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-14 px-3 border-green-500/50 text-green-400 hover:bg-green-500/20"
                              onClick={() => completeSet(setIndex)}
                              data-testid={`complete-set-${setIndex}`}
                              title="Marcar serie completada"
                            >
                              <CheckCircle2 className="h-5 w-5" />
                            </Button>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Exercise Notes */}
                  <div className="pt-2 border-t border-border">
                    <Label className="text-xs text-muted-foreground mb-1 block">NOTAS / OBSERVACIONES</Label>
                    <Textarea
                      placeholder="¿Cómo te sentiste? ¿Alguna molestia? ¿Subiste peso?"
                      value={currentLog.exercise_notes || ''}
                      onChange={(e) => updateLog(currentExerciseIndex, 'exercise_notes', e.target.value)}
                      className="bg-zinc-900 border-zinc-700 focus:border-primary resize-none"
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Bottom Action Button */}
            <div className="sticky bottom-0 p-4 bg-background/95 backdrop-blur border-t border-border">
              <Button 
                onClick={nextExercise}
                className="w-full h-14 text-lg font-bold bg-primary hover:bg-primary/90"
              >
                {currentExerciseIndex < exerciseLogs.length - 1 ? (
                  <>
                    SIGUIENTE EJERCICIO
                    <ChevronRight className="h-5 w-5 ml-2" />
                  </>
                ) : (
                  <>
                    FINALIZAR SESIÓN
                    <CheckCircle2 className="h-5 w-5 ml-2" />
                  </>
                )}
              </Button>
            </div>

            {/* Rest Timer Dialog */}
            {showRestTimer && (
              <Dialog open={showRestTimer} onOpenChange={setShowRestTimer}>
                <DialogContent className="bg-card border-border max-w-sm">
                  <div className="text-center py-6">
                    <Timer className="h-12 w-12 text-primary mx-auto mb-4" />
                    <h3 className="text-xl font-bold text-foreground mb-2">Tiempo de Descanso</h3>
                    <RestTimer 
                      initialSeconds={restSeconds} 
                      onComplete={() => {
                        goToNextExercise();
                      }}
                      autoStart={true}
                    />
                    <Button 
                      variant="ghost" 
                      onClick={goToNextExercise}
                      className="mt-4"
                    >
                      Saltar descanso
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            )}
            
            {/* Rest Mode - Descanso consciente entre series */}
            {showRestMode && restModeEnabled && (
              <RestMode
                seconds={restSeconds}
                exerciseName={currentLog?.exercise_name || ''}
                seriesNumber={currentSeriesIndex + 1}
                nextSeriesNumber={currentSeriesIndex + 2}
                onComplete={handleRestModeComplete}
                onSkip={handleRestModeSkip}
                program={restProgram}
              />
            )}

            {/* Conexión Cuerpo - capa entre ejercicios */}
            {showMindfulConnection && mindfulItem && (
              <MindfulConnection
                item={mindfulItem}
                onSkip={handleMindfulSkip}
                onComplete={handleMindfulComplete}
              />
            )}

            {/* Cierre Consciente - bloque final de sesión */}
            {showCooldownBlock && cooldownBlock && (
              <MindfulCooldownBlock
                block={cooldownBlock}
                onFinish={handleCooldownFinish}
                onSkipAll={handleCooldownSkipAll}
              />
            )}
          </div>
        </ClientLayout>
      );
    }

    // Cardio View
    if (currentLog.type === 'cardio') {
      return (
        <ClientLayout>
          <div className="min-h-screen flex flex-col">
            {/* Progress Header */}
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border p-3">
              <div className="flex items-center justify-between mb-2">
                <Button variant="ghost" size="sm" onClick={previousExercise}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                </Button>
                <span className="text-sm font-medium text-muted-foreground">
                  {currentExerciseIndex + 1} / {exerciseLogs.length}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setPhase('complete')}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <div className="flex-1 p-4 space-y-6">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/20 rounded-full">
                  <Heart className="h-4 w-4 text-red-400" />
                  <span className="text-sm text-red-400 font-medium">Cardio</span>
                </div>
                <h1 className="text-2xl font-bold text-foreground">{currentLog.machine}</h1>
                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                  <span>{currentLog.target_time}</span>
                  <span>•</span>
                  <span>{currentLog.intensity}</span>
                </div>
              </div>

              {currentLog.notes && (
                <Card className="bg-red-500/10 border-red-500/30">
                  <CardContent className="p-4">
                    <p className="text-sm text-foreground">{currentLog.notes}</p>
                  </CardContent>
                </Card>
              )}

              <Card className="bg-card border-border">
                <CardContent className="p-4 space-y-4">
                  <div>
                    <Label>Tiempo real (min)</Label>
                    <Input
                      type="text"
                      placeholder="ej: 20 min"
                      value={currentLog.actual_time || ''}
                      onChange={(e) => updateLog(currentExerciseIndex, 'actual_time', e.target.value)}
                      className="h-12 text-lg"
                    />
                  </div>
                  <div>
                    <Label>Notas</Label>
                    <Textarea
                      placeholder="¿Cómo te sentiste?"
                      value={currentLog.cardio_notes || ''}
                      onChange={(e) => updateLog(currentExerciseIndex, 'cardio_notes', e.target.value)}
                      rows={2}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="sticky bottom-0 p-4 bg-background/95 backdrop-blur border-t border-border">
              <Button onClick={nextExercise} className="w-full h-14 text-lg font-bold bg-primary">
                {currentExerciseIndex < exerciseLogs.length - 1 ? 'SIGUIENTE' : 'FINALIZAR'}
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </div>

          {/* Conexión Cuerpo + Cierre Consciente (también accesibles desde cardio) */}
          {showMindfulConnection && mindfulItem && (
            <MindfulConnection
              item={mindfulItem}
              onSkip={handleMindfulSkip}
              onComplete={handleMindfulComplete}
            />
          )}
          {showCooldownBlock && cooldownBlock && (
            <MindfulCooldownBlock
              block={cooldownBlock}
              onFinish={handleCooldownFinish}
              onSkipAll={handleCooldownSkipAll}
            />
          )}
        </ClientLayout>
      );
    }

    // Circuit View
    if (currentLog.type === 'circuit') {
      return (
        <ClientLayout>
          <div className="min-h-screen flex flex-col">
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border p-3">
              <div className="flex items-center justify-between mb-2">
                <Button variant="ghost" size="sm" onClick={previousExercise}>
                  <ChevronLeft className="h-4 w-4 mr-1" /> Anterior
                </Button>
                <span className="text-sm font-medium text-muted-foreground">
                  {currentExerciseIndex + 1} / {exerciseLogs.length}
                </span>
                <Button variant="ghost" size="sm" onClick={() => setPhase('complete')}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Progress value={progress} className="h-2" />
            </div>

            <div className="flex-1 p-4 space-y-6">
              <div className="text-center space-y-2">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/20 rounded-full">
                  <Zap className="h-4 w-4 text-purple-400" />
                  <span className="text-sm text-purple-400 font-medium">Circuito</span>
                </div>
                <h1 className="text-2xl font-bold text-foreground">{currentLog.name}</h1>
                <p className="text-muted-foreground">{currentLog.rounds} rondas</p>
              </div>

              <div className="space-y-2">
                {currentLog.exercises?.map((ex, idx) => (
                  <Card key={idx} className="bg-card border-border">
                    <CardContent className="p-3 flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center bg-purple-500/20 text-purple-400 rounded-full text-sm font-bold">
                        {idx + 1}
                      </span>
                      <div className="flex-1">
                        <p className="font-medium text-foreground">{ex.exercise_name || ex.name || '—'}</p>
                        <p className="text-xs text-muted-foreground">
                          {ex.reps ? `${ex.reps} reps` : ''}{ex.reps && ex.time ? ' · ' : ''}{ex.time || ''}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div>
                <Label>Rondas completadas</Label>
                <Input
                  type="number"
                  min="0"
                  max={currentLog.rounds}
                  value={currentLog.rounds_completed || ''}
                  onChange={(e) => updateLog(currentExerciseIndex, 'rounds_completed', e.target.value)}
                  className="h-12 text-lg"
                />
              </div>
            </div>

            <div className="sticky bottom-0 p-4 bg-background/95 backdrop-blur border-t border-border">
              <Button onClick={nextExercise} className="w-full h-14 text-lg font-bold bg-primary">
                {currentExerciseIndex < exerciseLogs.length - 1 ? 'SIGUIENTE' : 'FINALIZAR'}
                <ChevronRight className="h-5 w-5 ml-2" />
              </Button>
            </div>
          </div>

          {/* Conexión Cuerpo + Cierre Consciente (también accesibles desde circuito) */}
          {showMindfulConnection && mindfulItem && (
            <MindfulConnection
              item={mindfulItem}
              onSkip={handleMindfulSkip}
              onComplete={handleMindfulComplete}
            />
          )}
          {showCooldownBlock && cooldownBlock && (
            <MindfulCooldownBlock
              block={cooldownBlock}
              onFinish={handleCooldownFinish}
              onSkipAll={handleCooldownSkipAll}
            />
          )}
        </ClientLayout>
      );
    }

    // Fallback para tipo de ejercicio desconocido dentro de training
    return (
      <ClientLayout>
        <div className="flex flex-col items-center justify-center h-64 text-center p-4">
          <AlertTriangle className="h-12 w-12 text-amber-400 mb-4" />
          <h2 className="text-xl font-bold text-foreground mb-2">Tipo de ejercicio no reconocido</h2>
          <p className="text-muted-foreground mb-4">Tipo: {currentLog?.type || 'desconocido'}</p>
          <Button onClick={() => setPhase('summary')} variant="outline">
            Volver al resumen
          </Button>
        </div>
      </ClientLayout>
    );
  }

  // Si estamos en phase 'complete', mostrar pantalla con opciones
  if (phase === 'complete') {
    return (
      <ClientLayout>
        {completeDialogJsx}
        <div className="flex flex-col items-center justify-center h-64 text-center p-4">
          <Trophy className="h-16 w-16 text-primary mb-4" />
          <h2 className="text-2xl font-bold text-foreground mb-2">
            {showCompleteDialog ? 'Completando sesión...' : 'Sesión finalizada'}
          </h2>
          <p className="text-muted-foreground mb-4">
            {showCompleteDialog 
              ? 'Selecciona cómo te sientes y guarda tu progreso.'
              : 'Tu progreso ha sido registrado correctamente.'}
          </p>
          {!showCompleteDialog && (
            <div className="flex gap-2">
              <Button onClick={() => setPhase('summary')} className="bg-primary">
                Volver al resumen
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowCompleteDialog(true)}
              >
                Completar sesión
              </Button>
            </div>
          )}
        </div>
      </ClientLayout>
    );
  }

  // Fallback: Si llegamos aquí sin renderizar nada, mostrar el resumen
  return (
    <ClientLayout>
      {completeDialogJsx}
      <div className="flex flex-col items-center justify-center h-64 text-center p-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
        <p className="text-muted-foreground">Cargando sesión...</p>
        <Button 
          variant="outline" 
          onClick={() => {
            setPhase('summary');
            setShowCompleteDialog(false);
            fetchCurrentSession();
          }} 
          className="mt-4"
        >
          Recargar sesión
        </Button>
      </div>
    </ClientLayout>
  );
};

export default ClientCurrentSession;
